import { BASE_URL_LEGACY } from '../../axios/axios.constants';
import { AxiosService } from '../../axios/axios.service';
import { axiosInstance } from '../../axios/axios.singleton';
import { tAxiosCustomConfig } from '../../axios/axios.types';
import {
  tAcceptInvitationPayload,
  tAcceptInvitationResponse,
  tAddWorkspaceUserPayload,
  tAddWorkspaceUserResponse,
  tCurrentWorkspaceInfoResponse,
  tResendInvitationPayload,
  tResendInvitationResponse,
  tToggleUserInboxPayload,
  tUnlinkUserWorkspacePayload,
  tUpdateIsInactiveUserWorkspacePayload,
  tUpdateIsInactiveUserWorkspaceRequest,
  tUpdateIsInactiveUserWorkspaceResponse,
  tUpdateWorkspacePayload,
  tUpdateWorkspaceResponse,
  tWorkspaceAccessResponse,
  tWorkspaceAccessUserResponse,
  tWorkspaceRequest,
  tWorkspaceResponse,
  tWorkspacesResponse,
} from './Workspace.types';

export const WorkspaceService = {
  createWorkspace: async function (
    request: tWorkspaceRequest,
  ): Promise<tWorkspaceResponse> {
    try {
      const response = await AxiosService.post<
        tWorkspaceRequest,
        tWorkspaceResponse
      >('/auth/workspace', request);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getWorkspaceInfo: async function (
    workspaceId: string,
  ): Promise<tCurrentWorkspaceInfoResponse> {
    try {
      const response = await AxiosService.get<{
        data: tCurrentWorkspaceInfoResponse;
      }>(`/workspace/${workspaceId}`);

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getWorkspaces: async function (): Promise<tWorkspacesResponse> {
    try {
      const response = await AxiosService.get<{
        data: tWorkspacesResponse;
      }>(`/workspaces`);

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  updateWorkspace: async function (
    request: tUpdateWorkspacePayload,
    wid: string,
  ): Promise<tUpdateWorkspaceResponse> {
    const config = { isLegacy: true, headers: {} };
    try {
      const response = await AxiosService.post<
        tUpdateWorkspacePayload,
        tUpdateWorkspaceResponse
      >(`workspaces/ws/${wid}`, request, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  addWorkspaceUser: async function (
    request: tAddWorkspaceUserPayload,
    workspaceId: string,
  ): Promise<tAddWorkspaceUserResponse> {
    // BUG-801: no manual Authorization here — the axios interceptor attaches
    // the tab's live Firebase token; a localStorage copy could belong to
    // whichever account logged in last on this browser profile.
    const config = {
      isLegacy: true,
    };
    try {
      const response = await AxiosService.post<
        tAddWorkspaceUserPayload,
        { data: tAddWorkspaceUserResponse }
      >(`workspaces/ws/${workspaceId}/user`, request, config);

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getUserDataByToken: async function (
    token: string,
  ): Promise<tWorkspaceAccessUserResponse> {
    const config = {
      isLegacy: true,
      headers: { Authorization: token },
      skipAutoRedirect: true, // Let activation flow handle 401 errors with custom toast notification
    };
    try {
      const response = await AxiosService.get<tWorkspaceAccessUserResponse>(
        'workspaces/workspace/user/invitation',
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  toggleWorkspaceUserInbox: async function (
    workspaceId: string,
    userId: string,
    payload: tToggleUserInboxPayload,
  ): Promise<void> {
    const config = { isLegacy: true, headers: {} };
    try {
      const response = await AxiosService.post<tToggleUserInboxPayload, void>(
        `workspaces/ws/${workspaceId}/user/${userId}/inbox`,
        payload,
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // Moved from DashboardService (RET-1350) — the per-user availability flag
  // is a workspace-user mutation, and this service is its established home
  // (see toggleWorkspaceUserInbox above).
  updateIsInactiveUserWorkspace: async function (
    request: tUpdateIsInactiveUserWorkspaceRequest,
  ): Promise<tUpdateIsInactiveUserWorkspaceResponse> {
    const url = `/workspaces/ws/${request.workspaceId}/user/${request.userId}/update-inactive`;
    const payload = { is_inactive: request.isInactive };
    const config = { isLegacy: true, headers: undefined };
    const post = () =>
      AxiosService.post<
        tUpdateIsInactiveUserWorkspacePayload,
        tUpdateIsInactiveUserWorkspaceResponse
      >(url, payload, config);

    try {
      const firstResponse = await post();
      // Web scoket repeat call TODO: remove when web socket is implemented
      // correctly. Best-effort: the first call already persisted the flag,
      // so a failed repeat must not make the caller report failure and leave
      // the UI asserting the opposite of backend truth (RET-1350 review).
      try {
        return await post();
      } catch {
        return firstResponse;
      }
    } catch (error) {
      return Promise.reject(error);
    }
  },
  deleteWorkspaceUser: async function (
    workspaceId: string,
    userId: string,
  ): Promise<void> {
    const config = { isLegacy: true, headers: {} };
    try {
      await AxiosService.delete(
        `workspaces/ws/${workspaceId}/user/${userId}`,
        config,
      );
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  unlinkWorkspaceUser: async function (
    workspaceId: string,
    userId: string,
    payload: tUnlinkUserWorkspacePayload,
  ): Promise<void> {
    const config = { isLegacy: true, headers: {} };
    try {
      await AxiosService.post(
        `workspaces/ws/${workspaceId}/user/${userId}/unlink`,
        payload,
        config,
      );
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // TODO: Remove this function when WhatsApp API is fully integrated or we have feature flags
  getWidAccessToWaApi: async function (
    workspaceId: string,
  ): Promise<tWorkspaceAccessResponse> {
    const config = { isLegacy: true, headers: {} };

    try {
      const response = await AxiosService.get<tWorkspaceAccessResponse>(
        `/whatsapp-api/access/${workspaceId}`,
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  resendUserInvitation: async function (
    payload: tResendInvitationPayload,
  ): Promise<tResendInvitationResponse> {
    // BUG-801: Authorization comes from the axios interceptor (live token).
    const config = { isLegacy: true };

    try {
      const response = await AxiosService.post<
        tResendInvitationPayload,
        tResendInvitationResponse
      >('workspaces/workspace/user/invitation/resend', payload, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * The accept of an emailed link with a password, on workspace-service.
   *
   * BUG-800: sent through the axios instance directly. AxiosService logs the
   * body and the config of a call and attaches both to Sentry on a failure,
   * and this one carries the typed password and the link's token. Its
   * refusals are steps of the flow, which the page reads from the rejection,
   * and its 401 means the link expired or was replaced, not that a session
   * ended: it must not sign anybody out.
   */
  acceptUserInvitation: async function (
    token: string,
    payload: tAcceptInvitationPayload,
  ): Promise<tAcceptInvitationResponse> {
    const config: tAxiosCustomConfig = {
      baseURL: BASE_URL_LEGACY,
      headers: { Authorization: token },
      skipAutoRedirect: true,
      redactLog: true,
    };

    const response = await axiosInstance.post<tAcceptInvitationResponse>(
      'workspaces/workspace/user/invitation',
      payload,
      config,
    );

    return response.data;
  },
};
